import { Injectable, NgZone } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { API } from 'aws-amplify';
import { ConfigService } from '../../../../core/config';
import {
  CameraCachedEventsResponseContract,
  CameraOnDemandRequestContract,
  CameraOnDemandResponseContract,
  CameraOnDemandStatusContract,
  CameraStoredDispenseVideoContract,
} from '../_shared/on-demand-request';
import {
  AnalyticsSummaryContract,
  EventHistoryItemContract,
  FleetMachineContract,
  FleetSummaryContract,
  MachineClassificationContract,
  MachineDiagnosticsContract,
  MachineRestocksContract,
  MachineSummaryContract,
  TimelinePointContract,
} from './types';
import {
  CameraFillLevels,
  MachineLevelHistory,
  RestockComparisonResponse,
  RestockRange,
} from '../../../../core/machine-camera/_services/machine-camera.util';

export {
  CameraCachedEventContract,
  CameraCachedEventsResponseContract,
  CameraOnDemandAction,
  CameraOnDemandArtifactContract,
  CameraOnDemandRequestContract,
  CameraOnDemandResponseContract,
  CameraOnDemandStatusContract,
  CameraStoredDispenseVideoContract,
} from '../_shared/on-demand-request';

interface CameraFleetPayload {
  summary: FleetSummaryContract;
  machines: FleetMachineContract[];
}

interface ApiResponseWithData<T> {
  data?: T;
}

export interface CameraFillLevelTimeseriesResponseContract {
  machine_id: string;
  timeseries: TimelinePointContract[];
}

@Injectable({ providedIn: 'root' })
export class AuditApiService {
  private apiName: string;

  constructor(
    private configService: ConfigService,
    private ngZone: NgZone
  ) {
    this.apiName = this.configService.get('host');
  }

  private extractPayload<T>(response: unknown): T {
    const typed = response as ApiResponseWithData<T> & T;
    if (typed && typed.data) {
      return typed.data;
    }
    return typed as T;
  }

  private get<T>(path: string, queryStringParameters?: Record<string, string>): Observable<T> {
    return new Observable<T>((observer) => {
      API.get(this.apiName, path, {
        response: false,
        queryStringParameters,
      })
        .then((response) => {
          this.ngZone.run(() => {
            observer.next(this.extractPayload<T>(response));
            observer.complete();
          });
        })
        .catch((error) => {
          this.ngZone.run(() => observer.error(error));
        });
    });
  }

  getFleetDataSnapshot(): Observable<CameraFleetPayload> {
    return this.get<CameraFleetPayload>('/dashboard/audit/fleet');
  }

  getAnalyticsSummary(period?: string): Observable<AnalyticsSummaryContract> {
    const query = period ? { range: period } : undefined;
    return this.get<AnalyticsSummaryContract>('/dashboard/audit/analytics/summary', query);
  }

  getRestocks(period?: string): Observable<MachineRestocksContract> {
    const query = period ? { range: period } : undefined;
    return this.get<MachineRestocksContract>('/dashboard/audit/analytics/restocks', query);
  }

  getClassification(period?: string): Observable<MachineClassificationContract> {
    const query = period ? { range: period } : undefined;
    return this.get<MachineClassificationContract>('/dashboard/audit/analytics/classification', query);
  }

  getDiagnostics(period?: string): Observable<MachineDiagnosticsContract> {
    const query = period ? { range: period } : undefined;
    return this.get<MachineDiagnosticsContract>('/dashboard/audit/analytics/diagnostics', query);
  }

  getMachineSummary(machineId: string, period?: string): Observable<MachineSummaryContract | null> {
    const query = period ? { range: period } : undefined;
    return this.get<MachineSummaryContract>(`/dashboard/audit/machine/${machineId}/summary`, query);
  }

  getMachineRestocks(machineId: string, period?: string): Observable<MachineRestocksContract> {
    const query = period ? { range: period } : undefined;
    return this.get<MachineRestocksContract>(`/dashboard/audit/machine/${machineId}/restocks`, query);
  }

  getMachineClassification(machineId: string, period?: string): Observable<MachineClassificationContract> {
    const query = period ? { range: period } : undefined;
    return this.get<MachineClassificationContract>(`/dashboard/audit/machine/${machineId}/classification`, query);
  }

  getMachineDiagnostics(machineId: string, period?: string): Observable<MachineDiagnosticsContract> {
    const query = period ? { range: period } : undefined;
    return this.get<MachineDiagnosticsContract>(`/dashboard/audit/machine/${machineId}/diagnostics`, query);
  }

  createCameraOnDemandRequest(request: CameraOnDemandRequestContract): Observable<CameraOnDemandResponseContract> {
    return new Observable<CameraOnDemandResponseContract>((observer) => {
      API.post(this.apiName, '/dashboard/camera/on-demand', {
        response: false,
        body: request,
      })
        .then((response) => {
          this.ngZone.run(() => {
            observer.next(this.extractPayload<CameraOnDemandResponseContract>(response));
            observer.complete();
          });
        })
        .catch((error) => {
          this.ngZone.run(() => observer.error(error));
        });
    });
  }

  getCameraOnDemandRequest(requestId: string): Observable<CameraOnDemandStatusContract> {
    return this.get<CameraOnDemandStatusContract>(`/dashboard/camera/on-demand/${requestId}`);
  }

  getMachineCachedEvents(
    machineId: string,
    kind: 'cached_event' | 'dispense_video'
  ): Observable<CameraCachedEventsResponseContract> {
    return this.get<CameraCachedEventsResponseContract>(
      `/dashboard/camera/machines/${machineId}/cached-events`,
      { kind }
    );
  }

  getStoredDispenseVideo(
    machineId: string,
    eventArtifactKey: string
  ): Observable<CameraStoredDispenseVideoContract> {
    return this.get<CameraStoredDispenseVideoContract>(
      `/dashboard/camera/machines/${machineId}/cached-events/dispense-video/${encodeURIComponent(eventArtifactKey)}`
    );
  }

  // A machine's own pages: the camera's latest reading per canister.
  getMachineCameraFillLevels(machineId: string): Observable<CameraFillLevels> {
    return this.get<CameraFillLevels>(`/dashboard/camera/machines/${encodeURIComponent(machineId)}/fill-levels`);
  }

  // A machine's own pages: restocks the machine reported next to those the camera detected.
  getMachineCameraRestocks(machineId: string, range: RestockRange): Observable<RestockComparisonResponse> {
    return this.get<RestockComparisonResponse>(
      `/dashboard/camera/machines/${encodeURIComponent(machineId)}/restocks`,
      { range }
    );
  }

  // A machine's own pages: the machine's level for one canister over time.
  getMachineLevelHistory(machineId: string, slotNo: number, range: RestockRange): Observable<MachineLevelHistory> {
    return this.get<MachineLevelHistory>(
      `/dashboard/camera/machines/${encodeURIComponent(machineId)}/machine-level-history`,
      { range, slot_no: String(slotNo) }
    );
  }

  getMachineFillLevelTimeseries(
    machineId: string,
    period?: string
  ): Observable<CameraFillLevelTimeseriesResponseContract> {
    const query = period ? { range: period } : undefined;
    return this.get<CameraFillLevelTimeseriesResponseContract>(
      `/dashboard/audit/machine/${machineId}/fill-level-timeseries`,
      query
    );
  }

  getEvents(machineId: string, period?: string, type?: string): Observable<EventHistoryItemContract[]> {
    const queryStringParameters: { range?: string; type?: string } = {};
    if (period) {
      queryStringParameters.range = period;
    }
    if (type) {
      queryStringParameters.type = type;
    }

    return this.get<{ events?: EventHistoryItemContract[] }>(
      `/dashboard/audit/machine/${machineId}/events`,
      queryStringParameters
    ).pipe(
      map((payload) => (payload && Array.isArray(payload.events) ? payload.events : []))
    );
  }
}
